import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatIDR, formatApiError } from "../lib/api";
import RoomUnitCost from "./RoomUnitCost";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const KIND_LABEL = { lab: "Laboratorium", radiology: "Radiologi", jasa: "Jasa Tindakan", fasilitas: "Fasilitas" };

const RADIOLOGY_DEP_CATEGORIES = [
    "X-Ray",
    "USG",
    "CT Scan",
    "MRI",
    "Mamografi",
    "Fluoroskopi",
    "Densitometri Tulang",
    "Kedokteran Nuklir",
    "Echocardiography",
];

// Label kategori pelayanan sesuai alat (X-Ray → Rontgen)
const RADIOLOGY_CAT_LABELS = { "X-Ray": "Rontgen (X-Ray)" };
const catLabel = (c) => RADIOLOGY_CAT_LABELS[c] || c;

// Fasilitas: kategori ruangan untuk volume & penyusutan per kategori
const FASILITAS_DEP_CATEGORIES = [
    "Ruang Kelas 3",
    "Ruang Kelas 2",
    "Ruang Kelas 1",
    "Ruang VIP",
    "Ruang VVIP",
    "NON Kelas",
    "HCU",
    "ICU",
    "Perinatologi",
    "ISOLASI",
    "NICU",
    "Ruang OK",
    "Ruang VK",
];

// Daftar kategori per jenis unit cost (null = tanpa kategori)
const catsFor = (kind) =>
    kind === "radiology"
        ? RADIOLOGY_DEP_CATEGORIES
        : kind === "fasilitas"
          ? FASILITAS_DEP_CATEGORIES
          : null;

const ALL_UC_CATEGORIES = [...RADIOLOGY_DEP_CATEGORIES, ...FASILITAS_DEP_CATEGORIES];

function emptyDepByCategory() {
    const o = {};
    for (const c of ALL_UC_CATEGORIES) o[c] = [0, 0, 0];
    return o;
}

function emptyVolByCategory() {
    const o = {};
    for (const c of ALL_UC_CATEGORIES) o[c] = [0, 0, 0];
    return o;
}

function Section({ title, description, children }) {
    return (
        <div className="surface-card p-6">
            <div className="mb-4">
                <h3 className="font-heading text-lg font-medium">{title}</h3>
                {description && (
                    <p className="text-sm text-[#4A5E57] mt-1 leading-relaxed">{description}</p>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function NumField({ label, value, onChange, testid, suffix, help }) {
    return (
        <div>
            <Label className="text-[#1A362D]">{label}</Label>
            <div className="relative mt-1">
                <Input
                    type="number"
                    value={value}
                    data-testid={testid}
                    onChange={(e) => onChange(e.target.value)}
                    className="pr-14"
                />
                {suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6A9C89] uppercase tracking-widest">
                        {suffix}
                    </span>
                )}
            </div>
            {help && <div className="text-xs text-[#6A9C89] mt-1">{help}</div>}
        </div>
    );
}

const empty = {
    categories: [],
    volume_3month: 0,
    volume_months: [0, 0, 0],
    total_omset_hospital: 0,
    unit_omset: 0,
    total_salary_hospital: 0,
    unit_salary: 0,
    salary_months: [0, 0, 0],
    bmhp_total: 0,
    bmhp_months: [0, 0, 0],
    total_depreciation_hospital: 0,
    unit_depreciation: 0,
    depreciation_months: [0, 0, 0],
    depreciation_by_category: emptyDepByCategory(),
    volume_by_category: emptyVolByCategory(),
    total_utilities_hospital: 0,
    unit_area_ratio: 0,
    utilities_ratio_by_category: {},
    omset_ratio_by_category: {},
    salary_by_category: {},
    bmhp_by_category: {},
};

const NUMERIC_KEYS = [
    "volume_3month",
    "total_omset_hospital",
    "unit_omset",
    "total_salary_hospital",
    "unit_salary",
    "bmhp_total",
    "total_depreciation_hospital",
    "unit_depreciation",
    "total_utilities_hospital",
    "unit_area_ratio",
];
const ARRAY_KEYS = ["volume_months", "salary_months", "bmhp_months", "depreciation_months"];

function MonthlyGroup({ title, value = [0, 0, 0], onChange, testidPrefix, suffix = "Rp" }) {
    const arr = [0, 1, 2].map((i) => Number(value[i]) || 0);
    const avg = (arr[0] + arr[1] + arr[2]) / 3;
    const total = arr[0] + arr[1] + arr[2];
    return (
        <div className="col-span-1 md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {["Bulan 1", "Bulan 2", "Bulan 3"].map((label, i) => (
                    <div key={label}>
                        <Label className="text-[#1A362D]">{label}</Label>
                        <div className="relative mt-1">
                            <Input
                                type="number"
                                value={arr[i]}
                                data-testid={`${testidPrefix}-m${i + 1}`}
                                onChange={(e) => {
                                    const next = [...arr];
                                    next[i] = Number(e.target.value) || 0;
                                    onChange(next);
                                }}
                                className="pr-14"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6A9C89] uppercase tracking-widest">
                                {suffix}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-md bg-[#F7F9F6] border border-[#D1DCD8] px-3 py-2">
                    <div className="text-xs text-[#6A9C89] uppercase tracking-widest">
                        Total 3 Bulan
                    </div>
                    <div
                        className="font-heading text-lg font-medium text-[#1A362D]"
                        data-testid={`${testidPrefix}-total`}
                    >
                        {suffix === "Rp" ? formatIDR(total) : total.toLocaleString("id-ID")}
                    </div>
                </div>
                <div className="rounded-md bg-[#E9EFEA] border border-[#C4DAD2] px-3 py-2">
                    <div className="text-xs text-[#245B4B] uppercase tracking-widest">
                        Rata-rata / bulan
                    </div>
                    <div
                        className="font-heading text-lg font-medium text-[#1A362D]"
                        data-testid={`${testidPrefix}-avg`}
                    >
                        {suffix === "Rp"
                            ? formatIDR(avg)
                            : avg.toLocaleString("id-ID", { maximumFractionDigits: 1 })}
                    </div>
                </div>
            </div>
            {title && (
                <div className="text-xs text-[#6A9C89] mt-2">{title}</div>
            )}
        </div>
    );
}

function CategoryChips({ categories, selected, onSelect, hasData, testidPrefix, onAdd, onRename, onDelete }) {
    const manage = !!(onAdd || onRename || onDelete);
    return (
        <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((c) => {
                const active = selected === c;
                const has = hasData(c);
                return (
                    <div
                        key={c}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelect(c)}
                        onKeyDown={(e) => e.key === "Enter" && onSelect(c)}
                        data-testid={`${testidPrefix}-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`group inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 border cursor-pointer ${
                            active
                                ? "bg-[#245B4B] text-white border-[#245B4B]"
                                : "bg-white text-[#245B4B] border-[#D1DCD8] hover:bg-[#E9EFEA]"
                        }`}
                    >
                        {catLabel(c)}
                        {has && !active && (
                            <span className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-[#6A9C89]" />
                        )}
                        {manage && (
                            <>
                                <button
                                    type="button"
                                    title="Ubah nama kategori"
                                    data-testid={`${testidPrefix}-rename-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRename(c);
                                    }}
                                    className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                        active ? "hover:bg-white/20" : "hover:bg-[#D1DCD8]/60"
                                    }`}
                                >
                                    <Pencil size={11} />
                                </button>
                                <button
                                    type="button"
                                    title="Hapus kategori"
                                    data-testid={`${testidPrefix}-delete-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(c);
                                    }}
                                    className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                        active ? "hover:bg-white/20" : "text-[#B3432B] hover:bg-[#D1DCD8]/60"
                                    }`}
                                >
                                    <Trash2 size={11} />
                                </button>
                            </>
                        )}
                    </div>
                );
            })}
            {manage && (
                <button
                    type="button"
                    onClick={onAdd}
                    data-testid={`${testidPrefix}-add-btn`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-[#6A9C89] text-[#245B4B] hover:bg-[#E9EFEA] transition-colors"
                >
                    <Plus size={12} /> Tambah Kategori
                </button>
            )}
        </div>
    );
}

export default function UnitCostPage() {
    const { kind } = useParams();
    const hasCategories = !!catsFor(kind); // radiologi & fasilitas: volume/penyusutan per kategori
    const isFasilitas = kind === "fasilitas";
    const masterLabel = isFasilitas ? "Master Fasilitas" : "Master Radiologi";
    const [params, setParams] = useState(empty);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selectedCat, setSelectedCat] = useState(RADIOLOGY_DEP_CATEGORIES[0]);
    const [fasTab, setFasTab] = useState("ruangan"); // fasilitas: "ruangan" | "kamar"

    // Fasilitas: kategori alat dapat dikelola pengguna (tambah/ubah/hapus)
    const catList =
        isFasilitas && (params.categories || []).length > 0
            ? params.categories
            : catsFor(kind);

    async function load() {
        setLoading(true);
        try {
            const res = await api.get(`/unit-cost/${kind}`);
            const { params: p, result: r } = res.data;
            const merged = { ...empty, ...p };
            merged.categories =
                kind === "fasilitas"
                    ? Array.isArray(p.categories) && p.categories.length
                        ? p.categories
                        : [...FASILITAS_DEP_CATEGORIES]
                    : [];
            const shapeCats = [...new Set([...ALL_UC_CATEGORIES, ...merged.categories])];
            for (const k of ARRAY_KEYS) {
                const a = Array.isArray(merged[k]) ? merged[k] : [];
                merged[k] = [0, 1, 2].map((i) => Math.round(Number(a[i]) || 0));
            }
            // Ensure per-category depreciation shape
            const dbc = { ...emptyDepByCategory(), ...(merged.depreciation_by_category || {}) };
            for (const c of shapeCats) {
                const a = Array.isArray(dbc[c]) ? dbc[c] : [];
                dbc[c] = [0, 1, 2].map((i) => Math.round(Number(a[i]) || 0));
            }
            merged.depreciation_by_category = dbc;
            // Ensure per-category volume shape
            const vbc = { ...emptyVolByCategory(), ...(merged.volume_by_category || {}) };
            for (const c of shapeCats) {
                const a = Array.isArray(vbc[c]) ? vbc[c] : [];
                vbc[c] = [0, 1, 2].map((i) => Math.round(Number(a[i]) || 0));
            }
            merged.volume_by_category = vbc;
            merged.utilities_ratio_by_category = { ...(merged.utilities_ratio_by_category || {}) };
            merged.omset_ratio_by_category = { ...(merged.omset_ratio_by_category || {}) };
            // Ensure per-category salary & BMHP shape
            for (const field of ["salary_by_category", "bmhp_by_category"]) {
                const d = { ...(merged[field] || {}) };
                for (const c of shapeCats) {
                    const a = Array.isArray(d[c]) ? d[c] : [];
                    d[c] = [0, 1, 2].map((i) => Math.round(Number(a[i]) || 0));
                }
                merged[field] = d;
            }
            setParams(merged);
            setResult(r);
            if (kind === "fasilitas") {
                setSelectedCat(merged.categories[0] || FASILITAS_DEP_CATEGORIES[0]);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        setSelectedCat(catsFor(kind)?.[0] ?? RADIOLOGY_DEP_CATEGORIES[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    function update(key, val) {
        setParams((p) => {
            const next = { ...p, [key]: val };
            if (key === "volume_months") {
                next.volume_3month = (val[0] || 0) + (val[1] || 0) + (val[2] || 0);
            } else if (key === "salary_months") {
                const sum = (val[0] || 0) + (val[1] || 0) + (val[2] || 0);
                next.unit_salary = sum;
                next.total_salary_hospital = sum;
            } else if (key === "bmhp_months") {
                next.bmhp_total = (val[0] || 0) + (val[1] || 0) + (val[2] || 0);
            } else if (key === "depreciation_months") {
                const sum = (val[0] || 0) + (val[1] || 0) + (val[2] || 0);
                next.unit_depreciation = sum;
                next.total_depreciation_hospital = sum;
            }
            setResult(computeLocal(next));
            return next;
        });
    }

    function updateVolCategory(cat, months) {
        setParams((p) => {
            const vbc = { ...(p.volume_by_category || {}), [cat]: months };
            // volume_months & volume_3month = penjumlahan seluruh kategori
            const sums = [0, 0, 0];
            for (const c of catList || ALL_UC_CATEGORIES) {
                const a = vbc[c] || [0, 0, 0];
                for (let i = 0; i < 3; i++) sums[i] += Number(a[i]) || 0;
            }
            const next = {
                ...p,
                volume_by_category: vbc,
                volume_months: sums,
                volume_3month: sums[0] + sums[1] + sums[2],
            };
            setResult(computeLocal(next));
            return next;
        });
    }

    function updateDepCategory(cat, months) {
        setParams((p) => {
            const dbc = { ...(p.depreciation_by_category || {}), [cat]: months };
            const next = { ...p, depreciation_by_category: dbc };
            // For radiology, primary depreciation_months reflects the currently selected category
            if (cat === selectedCat) {
                next.depreciation_months = months;
                const sum = (months[0] || 0) + (months[1] || 0) + (months[2] || 0);
                next.unit_depreciation = sum;
                next.total_depreciation_hospital = sum;
            }
            setResult(computeLocal(next));
            return next;
        });
    }

    function updateRatioCategory(field, cat, val) {
        setParams((p) => {
            const next = { ...p, [field]: { ...(p[field] || {}), [cat]: val } };
            setResult(computeLocal(next));
            return next;
        });
    }

    function updateMonthsCategory(field, cat, months) {
        setParams((p) => {
            const next = { ...p, [field]: { ...(p[field] || {}), [cat]: months } };
            setResult(computeLocal(next));
            return next;
        });
    }

    // ----- Kelola kategori alat (fasilitas): tambah / ubah nama / hapus -----
    const BY_CAT_DICTS = [
        "volume_by_category",
        "depreciation_by_category",
        "salary_by_category",
        "bmhp_by_category",
        "utilities_ratio_by_category",
        "omset_ratio_by_category",
    ];

    function setCategories(nextCats, mutateDict) {
        setParams((p) => {
            const next = { ...p, categories: nextCats };
            if (mutateDict) {
                for (const f of BY_CAT_DICTS) next[f] = mutateDict({ ...(p[f] || {}) });
            }
            // Total volume dihitung ulang dari kategori yang tersisa
            const sums = [0, 0, 0];
            for (const c of nextCats) {
                const a = next.volume_by_category?.[c] || [0, 0, 0];
                for (let i = 0; i < 3; i++) sums[i] += Number(a[i]) || 0;
            }
            next.volume_months = sums;
            next.volume_3month = sums[0] + sums[1] + sums[2];
            setResult(computeLocal(next));
            return next;
        });
    }

    function addCategory() {
        const name = window.prompt("Nama kategori alat baru (mis. Ventilator, Syringe Pump):");
        if (!name || !name.trim()) return;
        const n = name.trim();
        if (catList.some((c) => c.toLowerCase() === n.toLowerCase())) {
            toast.error(`Kategori "${n}" sudah ada`);
            return;
        }
        setCategories([...catList, n]);
        setSelectedCat(n);
    }

    function renameCategory(cat) {
        const name = window.prompt(`Ubah nama kategori "${cat}":`, cat);
        if (!name || !name.trim() || name.trim() === cat) return;
        const n = name.trim();
        if (catList.some((c) => c !== cat && c.toLowerCase() === n.toLowerCase())) {
            toast.error(`Kategori "${n}" sudah ada`);
            return;
        }
        setCategories(
            catList.map((c) => (c === cat ? n : c)),
            (d) => {
                if (cat in d) {
                    d[n] = d[cat];
                    delete d[cat];
                }
                return d;
            }
        );
        if (selectedCat === cat) setSelectedCat(n);
    }

    function deleteCategory(cat) {
        if (catList.length <= 1) {
            toast.error("Minimal harus ada satu kategori alat");
            return;
        }
        if (
            !window.confirm(
                `Hapus kategori "${cat}"? Data kapasitas, penyusutan, dan biaya kategori ini ikut terhapus.`
            )
        )
            return;
        const nextCats = catList.filter((c) => c !== cat);
        setCategories(nextCats, (d) => {
            delete d[cat];
            return d;
        });
        if (selectedCat === cat) setSelectedCat(nextCats[0]);
    }

    // When user switches selected category (radiology), sync the primary depreciation fields
    // so the sticky "Hasil Perhitungan" reflects that category.
    useEffect(() => {
        if (!hasCategories) return;
        const months = params.depreciation_by_category?.[selectedCat] || [0, 0, 0];
        setParams((p) => {
            const sum = (months[0] || 0) + (months[1] || 0) + (months[2] || 0);
            const next = {
                ...p,
                depreciation_months: months,
                unit_depreciation: sum,
                total_depreciation_hospital: sum,
            };
            setResult(computeLocal(next));
            return next;
        });
    }, [selectedCat, hasCategories]);

    async function save() {
        setSaving(true);
        try {
            const payload = { kind, ...normalize(params) };
            const res = await api.put(`/unit-cost/${kind}`, payload);
            setResult(res.data.result);
            toast.success("Unit cost tersimpan");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    async function applyToMaster() {
        const contoh = isFasilitas
            ? "(Ruang Kelas 3 ← Ruang Kelas 3, ICU ← ICU, Ruang OK ← Ruang OK, dst)"
            : "(Rontgen ← X-Ray, USG ← USG, dst)";
        if (
            !window.confirm(
                `Terapkan unit cost per kategori ke ${masterLabel}?\n\nUnit Cost Dasar SEMUA item ${KIND_LABEL[kind]} akan ditimpa sesuai UC kategorinya ${contoh}. Data unit cost akan disimpan terlebih dahulu.`
            )
        )
            return;
        setApplying(true);
        try {
            // Simpan dulu agar UC yang diterapkan sesuai input terbaru
            const payload = { kind, ...normalize(params) };
            const saved = await api.put(`/unit-cost/${kind}`, payload);
            setResult(saved.data.result);
            const res = await api.post(`/unit-cost/${kind}/apply-to-master`);
            const d = res.data;
            const detail = Object.entries(d.per_category || {})
                .map(([c, n]) => `${catLabel(c)}: ${n}`)
                .join(", ");
            toast.success(
                `${d.updated} pemeriksaan diperbarui${detail ? ` (${detail})` : ""}${
                    d.skipped ? `. ${d.skipped} dilewati` : ""
                }`
            );
            if (d.skipped > 0 && d.skipped_categories?.length) {
                toast.info(
                    `Kategori tidak dikenali / UC 0: ${d.skipped_categories.join(", ")}`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplying(false);
        }
    }

    if (loading) {
        return <div className="text-[#4A5E57]">Memuat...</div>;
    }

    // Hasil kategori terpilih (radiologi): biaya, volume, dan UC per kategori
    const selCatResult = hasCategories
        ? result?.results_by_category?.find((r) => r.category === selectedCat)
        : null;

    return (
        <div className="space-y-6" data-testid={`uc-${kind}-page`}>
            <div>
                <div className="label-uppercase mb-2">Master Unit Cost</div>
                <h1 className="font-heading text-4xl tracking-tight font-semibold">
                    Unit Cost {KIND_LABEL[kind] || kind}
                </h1>
                <p className="text-[#4A5E57] mt-2 max-w-3xl">
                    {isFasilitas && fasTab === "kamar"
                        ? "Unit Cost per Hari Rawat (Cost per Patient Day) per kelas kamar dengan metode Normative Unit Cost: biaya langsung + alokasi overhead dibagi kapasitas normatif (Bed × 365 × BOR)."
                        : isFasilitas
                          ? "Perhitungan unit cost pemakaian alat berdasarkan kapasitas normatif, BMHP, penyusutan per alat, biaya listrik (opsional), biaya maintenance/kalibrasi, serta proporsi biaya tidak langsung (perizinan, license, dll) selama 3 bulan terakhir."
                          : "Perhitungan unit cost per pemeriksaan berdasarkan proporsi gaji SDM, BMHP, penyusutan ruangan, biaya listrik & air (berdasarkan luas ruangan), serta proporsi biaya tidak langsung selama 3 bulan terakhir."}
                </p>
            </div>

            {isFasilitas && (
                <div className="flex gap-1 rounded-lg bg-[#E9EFEA] p-1 w-fit" data-testid="uc-fasilitas-tabs">
                    {[
                        { key: "ruangan", label: "Unit Cost Alat" },
                        { key: "kamar", label: "Unit Cost Kamar (per Hari Rawat)" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setFasTab(t.key)}
                            data-testid={`uc-tab-${t.key}`}
                            className={`px-4 py-2 rounded-md text-sm transition-colors ${
                                fasTab === t.key
                                    ? "bg-white text-[#1A362D] font-medium shadow-sm"
                                    : "text-[#4A5E57] hover:text-[#1A362D]"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            {isFasilitas && fasTab === "kamar" ? (
                <RoomUnitCost />
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Section
                        title={
                            hasCategories
                                ? isFasilitas
                                    ? "Kapasitas Normatif"
                                    : "Volume Output (per Kategori Pemeriksaan)"
                                : "Volume Output"
                        }
                        description={
                            hasCategories
                                ? isFasilitas
                                    ? "Kapasitas pemakaian alat yang normatif per alat. Pilih item lalu isi kapasitas pemakaian 3 bulan terakhir."
                                    : "Volume pemeriksaan dipisah per alat/kategori pelayanan (X-Ray = Rontgen, USG = USG, dst). Pilih kategori lalu isi volume 3 bulan terakhir — total volume unit dihitung otomatis dari seluruh kategori."
                                : "Masukkan volume pemeriksaan tiap bulan selama 3 bulan terakhir. Total dan rata-rata dihitung otomatis."
                        }
                    >
                        {hasCategories ? (
                            <div className="col-span-1 md:col-span-2">
                                <CategoryChips
                                    categories={catList}
                                    selected={selectedCat}
                                    onSelect={setSelectedCat}
                                    hasData={(c) =>
                                        (params.volume_by_category?.[c] || []).some(
                                            (v) => Number(v) > 0
                                        )
                                    }
                                    testidPrefix="uc-vol-cat"
                                    onAdd={isFasilitas ? addCategory : undefined}
                                    onRename={isFasilitas ? renameCategory : undefined}
                                    onDelete={isFasilitas ? deleteCategory : undefined}
                                />
                                <MonthlyGroup
                                    value={params.volume_by_category?.[selectedCat] || [0, 0, 0]}
                                    onChange={(v) => updateVolCategory(selectedCat, v)}
                                    testidPrefix="uc-volume"
                                    suffix="unit"
                                    title={`${isFasilitas ? "Kapasitas" : "Volume"} untuk kategori "${catLabel(selectedCat)}"`}
                                />
                                <div
                                    className="mt-3 rounded-md bg-[#F7F9F6] border border-[#D1DCD8] px-3 py-2 flex items-baseline justify-between"
                                    data-testid="uc-volume-all-total"
                                >
                                    <span className="text-xs text-[#6A9C89] uppercase tracking-widest">
                                        Total {isFasilitas ? "Kapasitas" : "Volume"} Seluruh Kategori (3 bulan)
                                    </span>
                                    <span className="font-heading text-lg font-medium text-[#1A362D]">
                                        {Number(params.volume_3month || 0).toLocaleString("id-ID")}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <MonthlyGroup
                                value={params.volume_months}
                                onChange={(v) => update("volume_months", v)}
                                testidPrefix="uc-volume"
                                suffix="unit"
                            />
                        )}
                    </Section>

                    {!isFasilitas && (
                    <Section
                        title="Gaji SDM"
                        description={
                            hasCategories
                                ? "Isi gaji SDM per kategori pemeriksaan di bawah. Jika semua kategori 0, dipakai input unit (alokasi proporsional volume)."
                                : `Masukkan biaya gaji SDM unit ${KIND_LABEL[kind]} tiap bulan selama 3 bulan terakhir.`
                        }
                    >
                        <MonthlyGroup
                            value={params.salary_months}
                            onChange={(v) => update("salary_months", v)}
                            testidPrefix="uc-salary"
                            suffix="Rp"
                            title={hasCategories ? "Gaji SDM tingkat unit (dipakai bila per kategori kosong)" : undefined}
                        />
                        {hasCategories && (
                            <div className="col-span-1 md:col-span-2">
                                <MonthlyGroup
                                    value={params.salary_by_category?.[selectedCat] || [0, 0, 0]}
                                    onChange={(v) =>
                                        updateMonthsCategory("salary_by_category", selectedCat, v)
                                    }
                                    testidPrefix="uc-salary-cat"
                                    suffix="Rp"
                                    title={`Gaji SDM khusus kategori "${catLabel(selectedCat)}" (ganti kategori lewat pilihan di bagian Volume/Penyusutan)`}
                                />
                            </div>
                        )}
                    </Section>
                    )}

                    <Section
                        title="BMHP"
                        description={
                            hasCategories
                                ? isFasilitas
                                    ? "Isi Bahan Habis Pakai per kategori alat di bawah (ganti kategori lewat pilihan di bagian Kapasitas Normatif)."
                                    : "Isi BMHP per kategori pemeriksaan di bawah. Jika semua kategori 0, dipakai input unit (alokasi proporsional volume)."
                                : "Masukkan biaya Bahan Medis Habis Pakai (BMHP) tiap bulan selama 3 bulan terakhir."
                        }
                    >
                        {!isFasilitas && (
                        <MonthlyGroup
                            value={params.bmhp_months}
                            onChange={(v) => update("bmhp_months", v)}
                            testidPrefix="uc-bmhp"
                            suffix="Rp"
                            title={hasCategories ? "BMHP tingkat unit (dipakai bila per kategori kosong)" : undefined}
                        />
                        )}
                        {hasCategories && (
                            <div className="col-span-1 md:col-span-2">
                                <MonthlyGroup
                                    value={params.bmhp_by_category?.[selectedCat] || [0, 0, 0]}
                                    onChange={(v) =>
                                        updateMonthsCategory("bmhp_by_category", selectedCat, v)
                                    }
                                    testidPrefix="uc-bmhp-cat"
                                    suffix="Rp"
                                    title={`BMHP khusus kategori "${catLabel(selectedCat)}" (ganti kategori lewat pilihan di bagian Volume/Penyusutan)`}
                                />
                            </div>
                        )}
                    </Section>

                    {hasCategories ? (
                        <Section
                            title={`Penyusutan (per ${isFasilitas ? "Alat" : "Kategori Pemeriksaan"})`}
                            description={
                                isFasilitas
                                    ? "Penyusutan alat berbeda-beda per kategori — pilih kategori lalu isi biaya penyusutan alat 3 bulan terakhir untuk masing-masing kategori."
                                    : "Alat radiologi berbeda per kategori — pilih kategori lalu isi biaya penyusutan 3 bulan terakhir untuk masing-masing kategori."
                            }
                        >
                            <div className="col-span-1 md:col-span-2">
                                <CategoryChips
                                    categories={catList}
                                    selected={selectedCat}
                                    onSelect={setSelectedCat}
                                    hasData={(c) =>
                                        (params.depreciation_by_category?.[c] || []).some(
                                            (v) => Number(v) > 0
                                        )
                                    }
                                    testidPrefix="uc-dep-cat"
                                />
                                <MonthlyGroup
                                    value={
                                        params.depreciation_by_category?.[selectedCat] || [
                                            0, 0, 0,
                                        ]
                                    }
                                    onChange={(v) => updateDepCategory(selectedCat, v)}
                                    testidPrefix="uc-dep"
                                    suffix="Rp"
                                    title={`Data penyusutan untuk kategori "${catLabel(selectedCat)}"`}
                                />
                            </div>
                        </Section>
                    ) : (
                        <Section
                            title="Penyusutan"
                            description={`Masukkan biaya penyusutan (ruangan & alat) unit ${KIND_LABEL[kind]} tiap bulan selama 3 bulan terakhir.`}
                        >
                            <MonthlyGroup
                                value={params.depreciation_months}
                                onChange={(v) => update("depreciation_months", v)}
                                testidPrefix="uc-dep"
                                suffix="Rp"
                            />
                        </Section>
                    )}

                    <Section
                        title={isFasilitas ? "Biaya Listrik (opsional)" : "Listrik & Air (Proporsi Luas Ruangan)"}
                        description={
                            isFasilitas
                                ? "Opsional — biaya listrik untuk pemakaian alat. Isi total biaya listrik RS beserta proporsinya, atau proporsi khusus per kategori. Biarkan 0 jika biaya listrik tidak dibebankan."
                                : "Biaya listrik & air rumah sakit dialokasikan proporsional terhadap luas ruangan unit."
                        }
                    >
                        <NumField
                            label={isFasilitas ? "Total Biaya Listrik RS" : "Total Biaya Listrik & Air RS"}
                            value={params.total_utilities_hospital}
                            onChange={(v) => update("total_utilities_hospital", v)}
                            testid="uc-total-util"
                            suffix="Rp"
                        />
                        <NumField
                            label="Proporsi Luas Ruangan Unit"
                            value={params.unit_area_ratio}
                            onChange={(v) => update("unit_area_ratio", v)}
                            testid="uc-area-ratio"
                            suffix="0-1"
                            help="Contoh: 0.08 = 8% dari total luas ruangan RS"
                        />
                        {hasCategories && (
                            <NumField
                                label={`Proporsi Listrik${isFasilitas ? "" : " & Air"} — ${catLabel(selectedCat)}`}
                                value={params.utilities_ratio_by_category?.[selectedCat] ?? 0}
                                onChange={(v) =>
                                    updateRatioCategory("utilities_ratio_by_category", selectedCat, v)
                                }
                                testid="uc-util-ratio-cat"
                                suffix="0-1"
                                help={`Proporsi konsumsi listrik${isFasilitas ? "" : " & air"} khusus kategori ini (ganti kategori lewat pilihan di bagian Volume/Penyusutan). Jika semua kategori 0, alokasi memakai proporsi unit di atas & volume.`}
                            />
                        )}
                    </Section>

                    {isFasilitas && (
                    <Section
                        title="Biaya Maintenance/Kalibrasi"
                        description="Biaya pemeliharaan (maintenance) dan kalibrasi alat per kategori. Ganti kategori lewat pilihan di bagian Kapasitas Normatif."
                    >
                        <div className="col-span-1 md:col-span-2">
                            <MonthlyGroup
                                value={params.salary_by_category?.[selectedCat] || [0, 0, 0]}
                                onChange={(v) =>
                                    updateMonthsCategory("salary_by_category", selectedCat, v)
                                }
                                testidPrefix="uc-salary-cat"
                                suffix="Rp"
                                title={`Biaya maintenance/kalibrasi khusus kategori "${catLabel(selectedCat)}"`}
                            />
                        </div>
                    </Section>
                    )}

                    <Section
                        title="Proporsi Biaya Tidak Langsung"
                        description={
                            isFasilitas
                                ? "Alokasi biaya tidak langsung rumah sakit ke unit — contoh: biaya perizinan, biaya license, administrasi, dan biaya penunjang lainnya."
                                : "Alokasi biaya tidak langsung rumah sakit ke unit berdasarkan persentase kontribusi biaya unit terhadap total biaya RS."
                        }
                    >
                        <NumField
                            label="Total Biaya RS"
                            value={params.total_omset_hospital}
                            onChange={(v) => update("total_omset_hospital", v)}
                            testid="uc-total-omset"
                            suffix="Rp"
                        />
                        <NumField
                            label={`Persentase Biaya Unit ${KIND_LABEL[kind]}`}
                            value={params.unit_omset}
                            onChange={(v) => update("unit_omset", v)}
                            testid="uc-unit-omset"
                            suffix="0-1"
                            help="Contoh: 0.15 = 15% dari total biaya RS"
                        />
                        {hasCategories && (
                            <NumField
                                label={`Persentase Biaya Tidak Langsung — ${catLabel(selectedCat)}`}
                                value={params.omset_ratio_by_category?.[selectedCat] ?? 0}
                                onChange={(v) =>
                                    updateRatioCategory("omset_ratio_by_category", selectedCat, v)
                                }
                                testid="uc-omset-ratio-cat"
                                suffix="0-1"
                                help="Persentase biaya tidak langsung khusus kategori ini. Jika semua kategori 0, alokasi memakai persentase unit di atas & volume."
                            />
                        )}
                    </Section>
                </div>

                <div className="lg:col-span-1">
                    <div className="surface-card p-6 sticky top-6">
                        <div className="label-uppercase mb-2">Ringkasan (Rata-rata / Bulan)</div>
                        <h3 className="font-heading text-xl font-medium mb-4">
                            Hasil Perhitungan
                        </h3>

                        <div className="space-y-2 text-sm border-b border-[#D1DCD8] pb-4 mb-4">
                            {hasCategories && (
                                <div className="text-xs text-[#6A9C89]">
                                    Rincian kategori: {catLabel(selectedCat)}
                                </div>
                            )}
                            <Row
                                label={isFasilitas ? "Kontribusi Maintenance/Kalibrasi" : "Kontribusi Gaji SDM"}
                                testid="uc-sum-salary"
                                value={
                                    hasCategories && selCatResult
                                        ? selCatResult.salary_monthly_avg
                                        : result?.contributions.salary
                                }
                            />
                            <Row
                                label="BMHP"
                                testid="uc-sum-bmhp"
                                value={
                                    hasCategories && selCatResult
                                        ? selCatResult.bmhp_monthly_avg
                                        : result?.contributions.bmhp
                                }
                            />
                            <Row
                                label="Penyusutan"
                                testid="uc-sum-dep"
                                value={
                                    hasCategories && selCatResult
                                        ? selCatResult.depreciation_monthly_avg
                                        : result?.contributions.depreciation
                                }
                            />
                            <Row
                                label={isFasilitas ? "Biaya Listrik" : "Listrik & Air"}
                                testid="uc-sum-util"
                                value={
                                    hasCategories && selCatResult
                                        ? selCatResult.utilities_monthly_avg
                                        : result?.contributions.utilities
                                }
                            />
                            <Row
                                label="Biaya Tidak Langsung"
                                testid="uc-sum-omset"
                                value={
                                    hasCategories && selCatResult
                                        ? selCatResult.indirect_monthly_avg
                                        : result?.contributions.omset
                                }
                            />
                        </div>

                        <div className="flex items-baseline justify-between mb-2">
                            <div className="text-sm text-[#4A5E57]">
                                Rata-rata Biaya / Bulan
                                {hasCategories && (
                                    <span className="block text-xs text-[#6A9C89]">
                                        {catLabel(selectedCat)}
                                    </span>
                                )}
                            </div>
                            <div className="font-heading font-medium" data-testid="uc-avg-cost">
                                {formatIDR(
                                    hasCategories && selCatResult
                                        ? selCatResult.total_cost
                                        : result?.total_cost
                                )}
                            </div>
                        </div>
                        <div className="flex items-baseline justify-between mb-6">
                            <div className="text-sm text-[#4A5E57]">
                                Rata-rata Volume / Bulan
                                {hasCategories && (
                                    <span className="block text-xs text-[#6A9C89]">
                                        {catLabel(selectedCat)}
                                    </span>
                                )}
                            </div>
                            <div className="font-heading font-medium" data-testid="uc-avg-volume">
                                {Math.round(
                                    (hasCategories && selCatResult
                                        ? (selCatResult.volume_3month || 0) / 3
                                        : result?.volume) || 0
                                ).toLocaleString("id-ID")}
                            </div>
                        </div>

                        <div className="p-4 rounded-md bg-[#E9EFEA] mb-4">
                            <div className="text-xs text-[#245B4B] uppercase tracking-widest mb-1">
                                Unit Cost per Pemeriksaan
                                {hasCategories && (
                                    <span className="ml-1 normal-case tracking-normal text-[#4A5E57]">
                                        ({catLabel(selectedCat)})
                                    </span>
                                )}
                            </div>
                            <div
                                className="font-heading text-3xl font-semibold text-[#1A362D]"
                                data-testid="uc-result-unit-cost"
                            >
                                {formatIDR(
                                    hasCategories
                                        ? selCatResult?.unit_cost ?? result?.unit_cost
                                        : result?.unit_cost
                                )}
                            </div>
                        </div>

                        {hasCategories && result?.results_by_category?.length > 0 && (
                            <div className="mb-4 border border-[#D1DCD8] rounded-md overflow-hidden">
                                <div className="px-3 py-2 bg-[#F7F9F6] label-uppercase border-b border-[#D1DCD8]">
                                    Unit Cost per Kategori
                                </div>
                                <div className="divide-y divide-[#D1DCD8]">
                                    {result.results_by_category.map((r) => (
                                        <button
                                            key={r.category}
                                            type="button"
                                            onClick={() => setSelectedCat(r.category)}
                                            data-testid={`uc-cat-row-${r.category
                                                .toLowerCase()
                                                .replace(/\s+/g, "-")}`}
                                            className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
                                                selectedCat === r.category
                                                    ? "bg-[#E9EFEA]"
                                                    : "hover:bg-[#F7F9F6]"
                                            }`}
                                        >
                                            <span className="text-[#1A362D]">
                                                {catLabel(r.category)}
                                                <span className="block text-xs text-[#6A9C89]">
                                                    Vol 3 bln:{" "}
                                                    {Math.round(
                                                        r.volume_3month || 0
                                                    ).toLocaleString("id-ID")}
                                                </span>
                                            </span>
                                            <span className="font-heading font-medium text-[#1A362D]">
                                                {formatIDR(r.unit_cost)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="uc-save-btn"
                            className="w-full bg-[#245B4B] hover:bg-[#1A4236] text-white"
                        >
                            {saving ? "Menyimpan..." : "Simpan Master Unit Cost"}
                        </Button>
                        {hasCategories && (
                            <>
                                <Button
                                    onClick={applyToMaster}
                                    disabled={applying || saving}
                                    variant="outline"
                                    data-testid="uc-apply-to-master-btn"
                                    className="w-full mt-2 border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    {applying
                                        ? "Menerapkan..."
                                        : `Terapkan ke ${masterLabel}`}
                                </Button>
                                <div className="text-xs text-[#6A9C89] mt-2">
                                    {isFasilitas
                                        ? "Mengisi Unit Cost Dasar semua item fasilitas sesuai UC kategori ruangannya (Kelas 1-3, VIP/VVIP, HCU, ICU, NICU, OK, VK, dll)."
                                        : "Mengisi Unit Cost Dasar semua pemeriksaan radiologi sesuai UC kategori alatnya (Rontgen ← X-Ray, USG ← USG, dst)."}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}

function Row({ label, value, testid }) {
    return (
        <div className="flex justify-between">
            <span className="text-[#4A5E57]">{label}</span>
            <span className="font-medium" data-testid={testid}>
                {formatIDR(value)}
            </span>
        </div>
    );
}

function normalize(p) {
    const out = {};
    out.categories = Array.isArray(p.categories)
        ? p.categories.map((c) => String(c).trim()).filter(Boolean)
        : [];
    const CATS = [...new Set([...ALL_UC_CATEGORIES, ...out.categories])];
    for (const k of NUMERIC_KEYS) out[k] = Number(p[k]) || 0;
    for (const k of ARRAY_KEYS) {
        const a = Array.isArray(p[k]) ? p[k] : [];
        out[k] = [0, 1, 2].map((i) => Number(a[i]) || 0);
    }
    // Preserve per-category depreciation dict
    const dbc = p.depreciation_by_category || {};
    const outDbc = {};
    for (const c of CATS) {
        const a = Array.isArray(dbc[c]) ? dbc[c] : [];
        outDbc[c] = [0, 1, 2].map((i) => Number(a[i]) || 0);
    }
    out.depreciation_by_category = outDbc;
    // Preserve per-category volume dict
    const vbc = p.volume_by_category || {};
    const outVbc = {};
    for (const c of CATS) {
        const a = Array.isArray(vbc[c]) ? vbc[c] : [];
        outVbc[c] = [0, 1, 2].map((i) => Number(a[i]) || 0);
    }
    out.volume_by_category = outVbc;
    // Preserve per-category ratio dicts (listrik & air / biaya tidak langsung)
    const urbc = p.utilities_ratio_by_category || {};
    const orbc = p.omset_ratio_by_category || {};
    out.utilities_ratio_by_category = {};
    out.omset_ratio_by_category = {};
    for (const c of CATS) {
        out.utilities_ratio_by_category[c] = Number(urbc[c]) || 0;
        out.omset_ratio_by_category[c] = Number(orbc[c]) || 0;
    }
    // Preserve per-category salary & BMHP dicts
    out.salary_by_category = {};
    out.bmhp_by_category = {};
    const sbc = p.salary_by_category || {};
    const bbc = p.bmhp_by_category || {};
    for (const c of CATS) {
        const sa = Array.isArray(sbc[c]) ? sbc[c] : [];
        const ba = Array.isArray(bbc[c]) ? bbc[c] : [];
        out.salary_by_category[c] = [0, 1, 2].map((i) => Number(sa[i]) || 0);
        out.bmhp_by_category[c] = [0, 1, 2].map((i) => Number(ba[i]) || 0);
    }
    out.kind = p.kind;
    if (p.kind === "fasilitas") {
        // Field tingkat unit (maintenance/kalibrasi & BMHP) telah dihapus dari UI
        // fasilitas — nolkan agar tidak memengaruhi perhitungan secara tersembunyi.
        out.salary_months = [0, 0, 0];
        out.unit_salary = 0;
        out.total_salary_hospital = 0;
        out.bmhp_months = [0, 0, 0];
        out.bmhp_total = 0;
    }
    return out;
}

function computeLocal(p) {
    const v = normalize(p);
    const propSalary = v.total_salary_hospital ? v.unit_salary / v.total_salary_hospital : 0;
    const propDep = v.total_depreciation_hospital
        ? v.unit_depreciation / v.total_depreciation_hospital
        : 0;
    const contribOmset = v.unit_omset * v.total_omset_hospital;
    const contribSalary = propSalary * v.total_salary_hospital;
    const contribDep = propDep * v.total_depreciation_hospital;
    const contribUtil = v.unit_area_ratio * v.total_utilities_hospital;
    const contribBmhp = v.bmhp_total;
    const total = contribOmset + contribSalary + contribDep + contribUtil + contribBmhp;
    const unit_cost = v.volume_3month ? total / v.volume_3month : 0;

    // Radiologi/Fasilitas: unit cost per kategori — biaya bersama dialokasikan
    // proporsional volume kategori, ditambah penyusutan kategori itu sendiri.
    let results_by_category = [];
    const localCats =
        v.kind === "fasilitas" && v.categories.length ? v.categories : catsFor(v.kind);
    if (localCats) {
        const urbc = v.utilities_ratio_by_category || {};
        const orbc = v.omset_ratio_by_category || {};
        const sbc = v.salary_by_category || {};
        const bbc = v.bmhp_by_category || {};
        const sumArr = (a) => (a || [0, 0, 0]).reduce((s, x) => s + (Number(x) || 0), 0);
        const hasCatUtil = localCats.some((c) => (Number(urbc[c]) || 0) > 0);
        const hasCatOmset = localCats.some((c) => (Number(orbc[c]) || 0) > 0);
        const hasCatSalary = localCats.some((c) => sumArr(sbc[c]) > 0);
        const hasCatBmhp = localCats.some((c) => sumArr(bbc[c]) > 0);
        let sharedBase = 0;
        if (!hasCatSalary) sharedBase += contribSalary;
        if (!hasCatBmhp) sharedBase += contribBmhp;
        if (!hasCatUtil) sharedBase += contribUtil;
        if (!hasCatOmset) sharedBase += contribOmset;
        const volByCat = {};
        let totalVolCat = 0;
        for (const c of localCats) {
            const m = v.volume_by_category[c] || [0, 0, 0];
            volByCat[c] = m[0] + m[1] + m[2];
            totalVolCat += volByCat[c];
        }
        results_by_category = localCats.map((c) => {
            const months = v.depreciation_by_category[c] || [0, 0, 0];
            const depTotal = months[0] + months[1] + months[2];
            const volCat = volByCat[c];
            const share = totalVolCat > 0 ? volCat / totalVolCat : 0;
            const utilCat = hasCatUtil
                ? (Number(urbc[c]) || 0) * v.total_utilities_hospital
                : contribUtil * (totalVolCat > 0 ? share : 1);
            const omsetCat = hasCatOmset
                ? (Number(orbc[c]) || 0) * v.total_omset_hospital
                : contribOmset * (totalVolCat > 0 ? share : 1);
            const salaryCat = hasCatSalary
                ? sumArr(sbc[c])
                : contribSalary * (totalVolCat > 0 ? share : 1);
            const bmhpCat = hasCatBmhp
                ? sumArr(bbc[c])
                : contribBmhp * (totalVolCat > 0 ? share : 1);
            let extra = 0;
            if (hasCatUtil) extra += utilCat;
            if (hasCatOmset) extra += omsetCat;
            if (hasCatSalary) extra += salaryCat;
            if (hasCatBmhp) extra += bmhpCat;
            let totalCat, uc;
            if (totalVolCat > 0) {
                totalCat = sharedBase * share + extra + depTotal;
                uc = volCat ? totalCat / volCat : 0;
            } else {
                // Fallback data lama: volume global
                totalCat = sharedBase + extra + depTotal;
                uc = v.volume_3month ? totalCat / v.volume_3month : 0;
            }
            return {
                category: c,
                volume_3month: volCat,
                depreciation_3month: depTotal,
                depreciation_monthly_avg: depTotal / 3,
                utilities_monthly_avg: utilCat / 3,
                indirect_monthly_avg: omsetCat / 3,
                salary_monthly_avg: salaryCat / 3,
                bmhp_monthly_avg: bmhpCat / 3,
                total_cost: totalCat / 3,
                unit_cost: uc,
            };
        });
    }

    return {
        contributions: {
            omset: contribOmset / 3,
            salary: contribSalary / 3,
            bmhp: contribBmhp / 3,
            depreciation: contribDep / 3,
            utilities: contribUtil / 3,
        },
        total_cost: total / 3,
        volume: v.volume_3month / 3,
        unit_cost,
        results_by_category,
    };
}
